import { useEffect, useRef, useState } from "react";
import { api, downloadCsv } from "../lib/api";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import EmailBodyEditor from "../components/EmailBodyEditor";
import { Link } from "react-router-dom";
import { ClientLink } from "./AdminClient";

const STATUS = [
  { v: "paid", l: "Payée" },
  { v: "letter_received", l: "Lettre reçue" },
  { v: "to_write", l: "À écrire" },
  { v: "sent", l: "Envoyée" },
  { v: "closed", l: "Terminée" },
];

export function Paid({ cents, at }) {
  if (cents === null || cents === undefined) return <span className="text-xs text-[#7C7064]">—</span>;
  const date = at ? new Date(at).toLocaleDateString("fr-FR") : null;
  return (
    <span className="whitespace-nowrap">
      {cents === 0 ? <span className="text-xs text-[#7B8A72]">offert</span> : <span className="font-serif">{(cents / 100).toFixed(2).replace(".", ",")} €</span>}
      {date && <span className="block text-[11px] text-[#7C7064]">le {date}</span>}
    </span>
  );
}

export function Address({ a, name }) {
  if (!a) return <span className="italic text-[#A86962] text-xs">Non renseignée</span>;
  return <span className="whitespace-pre-line text-xs text-[#7C7064]">{[
    a.recipient_name || a.full_name || name, a.line1, a.line2,
    `${a.postal_code || ""} ${a.city || ""}`.trim(), a.country, a.phone ? `☎ ${a.phone}` : "",
  ].filter(Boolean).join("\n")}</span>;
}

export function AdminCorrespondances() {
  const [data, setData] = useState({ items: [], count_this_month: 0, max: 30 });

  async function load() {
    try { const r = await api.get("/admin/correspondances"); setData(r.data); }
    catch { toast.error("Erreur de chargement"); }
  }
  useEffect(() => { load(); }, []);

  async function updateStatus(id, status) {
    try {
      await api.patch(`/admin/correspondances/${id}/status`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch { toast.error("Erreur"); }
  }

  return (
    <div data-testid="admin-correspondances">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-hand text-xl text-[#A3876A]">Ce mois-ci</p>
          <h1 className="font-serif text-4xl">Correspondances</h1>
        </div>
        <div data-testid="admin-counter" className="text-right">
          <p className="font-serif text-4xl">{data.count_this_month}<span className="text-[#7C7064]"> / {data.max}</span></p>
          <p className="text-xs text-[#7C7064]">Compteur mensuel</p>
        </div>
      </div>

      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Cliente</th><th className="p-3">Email</th><th className="p-3">Formule</th>
            <th className="p-3">Payé</th><th className="p-3">Adresse postale</th><th className="p-3">Statut</th><th className="p-3">Date</th><th className="p-3">Action</th>
          </tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-[#7C7064]">Aucune correspondance pour le moment.</td></tr>}
            {data.items.map(c => (
              <tr key={c.correspondance_id} className="border-t border-[#E8DCC8]">
                <td className="p-3">{c.customer_name || "—"}</td>
                <td className="p-3 text-[#7C7064]"><ClientLink email={c.email}/></td>
                <td className="p-3">{c.product_type}</td>
                <td className="p-3" data-testid={`paid-${c.correspondance_id}`}><Paid cents={c.amount_paid} at={c.paid_at}/></td>
                <td className="p-3 align-top" data-testid={`addr-${c.correspondance_id}`}><Address a={c.shipping_address} name={c.customer_name}/></td>
                <td className="p-3">{STATUS.find(s=>s.v===c.status)?.l || c.status}</td>
                <td className="p-3 text-xs text-[#7C7064]">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="p-3">
                  <select data-testid={`status-select-${c.correspondance_id}`} value={c.status} onChange={e=>updateStatus(c.correspondance_id, e.target.value)}
                    className="text-xs border border-[#E8DCC8] bg-white px-2 py-1">
                    {STATUS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminCoffrets() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/coffrets").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  async function markShipped(id) {
    try { await api.patch(`/admin/coffrets/${id}/shipped`); toast.success("Coffret marqué envoyé"); load(); }
    catch { toast.error("Erreur"); }
  }
  return (
    <div data-testid="admin-coffrets">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl">Coffrets à envoyer</h1>
        <Link data-testid="coffrets-labels-link" to="/admin/etiquettes?scope=coffrets" className="btn-outline">Imprimer les étiquettes</Link>
      </div>
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Cliente</th><th className="p-3">Email</th><th className="p-3">Type</th>
            <th className="p-3">Payé</th><th className="p-3">Statut</th><th className="p-3">Adresse</th><th className="p-3">Action</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#7C7064]">Aucun coffret en attente.</td></tr>}
            {items.map(c => (
              <tr key={c.coffret_id} className="border-t border-[#E8DCC8] align-top">
                <td className="p-3">{c.customer_name || "—"}</td>
                <td className="p-3 text-[#7C7064]"><ClientLink email={c.email}/></td>
                <td className="p-3">{c.product_type}</td>
                <td className="p-3" data-testid={`paid-${c.coffret_id}`}><Paid cents={c.amount_paid} at={c.paid_at}/></td>
                <td className="p-3">{c.status === "shipped" ? "Envoyé" : "À envoyer"}</td>
                <td className="p-3 text-xs text-[#7C7064] whitespace-pre-line" data-testid={`addr-${c.coffret_id}`}>{c.shipping_address ? [
                  c.shipping_address.recipient_name || c.shipping_address.full_name || c.customer_name,
                  c.shipping_address.line1,
                  c.shipping_address.line2,
                  `${c.shipping_address.postal_code || ""} ${c.shipping_address.city || ""}`.trim(),
                  c.shipping_address.country,
                  c.shipping_address.phone ? `☎ ${c.shipping_address.phone}` : "",
                ].filter(Boolean).join("\n") : <span className="italic text-[#A86962]">Non renseignée — commande antérieure</span>}</td>
                <td className="p-3">
                  {c.status !== "shipped" && (
                    <button data-testid={`ship-${c.coffret_id}`} onClick={()=>markShipped(c.coffret_id)} className="text-xs bg-[#A3876A] text-white px-3 py-1.5">Marquer envoyé</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminCodes() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/codes").then(r => setItems(r.data.items)); }, []);
  return (
    <div data-testid="admin-codes">
      <h1 className="font-serif text-4xl mb-8">Codes correspondance</h1>
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Code</th><th className="p-3">Cliente</th><th className="p-3">État</th>
            <th className="p-3">Émis</th><th className="p-3">Expire</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-[#7C7064]">Aucun code émis pour le moment.</td></tr>}
            {items.map(c => (
              <tr key={c.code} className="border-t border-[#E8DCC8]">
                <td className="p-3 font-mono">{c.code}</td>
                <td className="p-3">{c.customer_name} <span className="text-[#7C7064] text-xs">({c.email})</span></td>
                <td className="p-3"><span className={`px-2 py-0.5 text-xs ${c.state==="used" ? "bg-[#DCCBBA]" : c.state==="expired" ? "bg-[#E8DCC8] text-[#A86962]" : "bg-[#7B8A72] text-white"}`}>{c.state}</span></td>
                <td className="p-3 text-xs text-[#7C7064]">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="p-3 text-xs text-[#7C7064]">{new Date(c.expires_at).toLocaleDateString("fr-FR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminGiftCards() {
  const [items, setItems] = useState([]);
  const [lookupCode, setLookupCode] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({
    email: "", name: "", lookup_key: "programme_seul",
    amount_paid: 0, reason: "Code promo 100% (AMIES-EN-OR)",
  });
  const [manualSending, setManualSending] = useState(false);

  const load = () => api.get("/admin/gift-cards").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const doLookup = async () => {
    const c = (lookupCode || "").trim().toUpperCase();
    if (!c) return;
    setLookupResult({loading: true});
    try {
      const r = await api.get(`/admin/codes/lookup/${encodeURIComponent(c)}`);
      setLookupResult(r.data);
    } catch (e) {
      setLookupResult({error: e.response?.data?.detail || "Erreur"});
    }
  };

  const doReconcile = async () => {
    if (!window.confirm("Vérifier auprès de Stripe toutes les cartes cadeaux 'pending' et les activer si elles sont bien payées ?")) return;
    setReconciling(true);
    try {
      const r = await api.post("/admin/gift-cards/reconcile");
      setReconcileResult(r.data);
      toast.success(`${r.data.reconciled.length} cartes réconciliées`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setReconciling(false); }
  };

  const doResendWelcome = async (code) => {
    if (!window.confirm(`Renvoyer l'email de bienvenue pour ${code} ?`)) return;
    try {
      const r = await api.post(`/admin/gift-cards/${code}/resend-welcome`);
      toast.success(`Email envoyé à ${r.data.sent.map(s => s.to).join(", ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const doManualOnboard = async () => {
    if (!manual.email || !manual.name) { toast.error("Email et prénom obligatoires"); return; }
    setManualSending(true);
    try {
      const r = await api.post("/admin/onboard/manual", {
        email: manual.email.trim(),
        name: manual.name.trim(),
        lookup_key: manual.lookup_key,
        amount_paid: Number(manual.amount_paid) || 0,
        reason: manual.reason,
      });
      toast.success(`${manual.name} inscrite à « ${r.data.product} » — email envoyé`);
      setManualOpen(false);
      setManual({...manual, email: "", name: ""});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setManualSending(false); }
  };

  return (
    <div data-testid="admin-gift-cards">
      <h1 className="font-serif text-4xl mb-8">Cartes cadeaux & codes</h1>

      {/* Outils de dépannage */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Lookup */}
        <div className="bg-[#FDFBF7] border border-[#E8DCC8] p-6">
          <h2 className="font-serif text-xl mb-2">🔍 Chercher un code</h2>
          <p className="text-sm text-[#7C7064] mb-4">Une cliente reçoit une erreur ? Tape le code, je te dis exactement pourquoi.</p>
          <div className="flex gap-2">
            <input
              data-testid="admin-code-lookup-input"
              className="field-input flex-1 font-mono uppercase"
              placeholder="ex : CADEAU-A1B2C3D4 ou AMIES-EN-OR"
              value={lookupCode}
              onChange={e=>setLookupCode(e.target.value)}
              onKeyDown={e=>e.key==="Enter" && doLookup()}
            />
            <button data-testid="admin-code-lookup-btn" onClick={doLookup} className="btn-primary">Chercher</button>
          </div>
          {lookupResult && (
            <div data-testid="admin-code-lookup-result" className="mt-4 p-4 bg-[#F5F0E6] border border-[#E8DCC8] text-sm">
              {lookupResult.loading && <p className="text-[#7C7064]">Recherche…</p>}
              {lookupResult.error && <p className="text-[#A86962]">✗ {lookupResult.error}</p>}
              {lookupResult.found_as && (
                <>
                  <p className="mb-2"><strong>Type :</strong> {lookupResult.found_as === "gift_card" ? "Carte cadeau achetée" : lookupResult.found_as === "promo_code" ? "Code promo (créé dans l'admin)" : "Code correspondance offert"}</p>
                  <p className="mb-2"><strong>Diagnostic :</strong> {lookupResult.diagnosis}</p>
                  {lookupResult.details?.buyer_email && <p className="text-xs text-[#7C7064]">Acheteur : {lookupResult.details.buyer_email}</p>}
                  {lookupResult.details?.recipient_email && <p className="text-xs text-[#7C7064]">Destinataire : {lookupResult.details.recipient_email}</p>}
                  {lookupResult.details?.used_by_email && <p className="text-xs text-[#7C7064]">Utilisé par : {lookupResult.details.used_by_email}</p>}
                </>
              )}
            </div>
          )}
        </div>

        {/* Reconcile */}
        <div className="bg-[#FDFBF7] border border-[#E8DCC8] p-6">
          <h2 className="font-serif text-xl mb-2">🔄 Réconcilier avec Stripe</h2>
          <p className="text-sm text-[#7C7064] mb-4">
            Corrige les cartes bloquées en "pending" à cause d'un webhook manqué : je re-demande à Stripe et j'active celles qui ont bien été payées.
          </p>
          <button
            data-testid="admin-reconcile-btn"
            onClick={doReconcile}
            disabled={reconciling}
            className="btn-primary"
          >{reconciling ? "Vérification en cours…" : "Lancer la réconciliation"}</button>
          {reconcileResult && (
            <div className="mt-4 text-sm">
              <p className="text-[#7B8A72]">✓ {reconcileResult.reconciled.length} cartes réactivées</p>
              {reconcileResult.reconciled.map(r => <p key={r.code} className="text-xs text-[#7C7064] ml-4">• {r.code} — {r.buyer_email}</p>)}
              {reconcileResult.errors.length > 0 && <p className="text-[#A86962] mt-2">✗ {reconcileResult.errors.length} erreurs Stripe (voir logs)</p>}
              {reconcileResult.skipped.length > 0 && <p className="text-[#7C7064] mt-2 text-xs">— {reconcileResult.skipped.length} ignorées (paiement Stripe pas confirmé)</p>}
            </div>
          )}
        </div>
      </div>

      {/* Inscription manuelle */}
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] p-6 mb-8">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h2 className="font-serif text-xl mb-1">✍️ Inscrire une cliente manuellement</h2>
            <p className="text-sm text-[#7C7064]">
              Pour un code promo 100 %, un paiement hors Stripe, ou un cas particulier — inscris la cliente sur le programme et je lui envoie tout automatiquement (email de bienvenue, facture si applicable).
            </p>
          </div>
          <button
            data-testid="admin-manual-onboard-toggle"
            onClick={()=>setManualOpen(v=>!v)}
            className={manualOpen ? "btn-outline" : "btn-primary"}
          >{manualOpen ? "Fermer" : "Nouveau"}</button>
        </div>
        {manualOpen && (
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <input
              data-testid="manual-onboard-name"
              className="field-input"
              placeholder="Prénom (ex : Camille)"
              value={manual.name}
              onChange={e=>setManual({...manual, name: e.target.value})}
            />
            <input
              data-testid="manual-onboard-email"
              className="field-input"
              type="email"
              placeholder="Email de la cliente"
              value={manual.email}
              onChange={e=>setManual({...manual, email: e.target.value})}
            />
            <select
              data-testid="manual-onboard-product"
              className="field-input"
              value={manual.lookup_key}
              onChange={e=>setManual({...manual, lookup_key: e.target.value})}
            >
              <option value="programme_seul">Programme Essentiel (102€)</option>
              <option value="programme_coffret">Programme Épistolaire (152€)</option>
              <option value="correspondance_simple">Correspondance Simple (35€)</option>
              <option value="correspondance_coffret">Correspondance + Coffret (59€)</option>
            </select>
            <input
              data-testid="manual-onboard-amount"
              className="field-input"
              type="number"
              placeholder="Montant payé (en centimes, 0 si gratuit)"
              value={manual.amount_paid}
              onChange={e=>setManual({...manual, amount_paid: e.target.value})}
            />
            <input
              data-testid="manual-onboard-reason"
              className="field-input md:col-span-2"
              placeholder="Motif (traçabilité — ex : code promo AMIES-EN-OR)"
              value={manual.reason}
              onChange={e=>setManual({...manual, reason: e.target.value})}
            />
            <div className="md:col-span-2 flex gap-2 justify-end">
              <button onClick={()=>setManualOpen(false)} className="btn-outline">Annuler</button>
              <button
                data-testid="manual-onboard-submit"
                onClick={doManualOnboard}
                disabled={manualSending}
                className="btn-primary"
              >{manualSending ? "Inscription en cours…" : "Inscrire et envoyer le mail"}</button>
            </div>
          </div>
        )}
      </div>

      {/* Table cartes cadeaux */}
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Code</th><th className="p-3">Acheteur</th><th className="p-3">Bénéficiaire</th>
            <th className="p-3">Produit</th><th className="p-3">Payé</th><th className="p-3">Mode</th><th className="p-3">Livraison</th>
            <th className="p-3">État</th><th className="p-3">Actions</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-[#7C7064]">Aucune carte cadeau pour l'instant.</td></tr>}
            {items.map(c => {
              const isDirect = c.send_mode === "direct";
              const scheduled = c.deliver_at ? new Date(c.deliver_at).toLocaleDateString("fr-FR") : (isDirect ? "immédiat" : "—");
              const delivered = c.delivered_at ? `envoyé le ${new Date(c.delivered_at).toLocaleDateString("fr-FR")}` : (isDirect ? `prévu ${scheduled}` : "impression");
              return (
                <tr key={c.code} className="border-t border-[#E8DCC8]">
                  <td className="p-3 font-mono text-xs">{c.code}</td>
                  <td className="p-3">{c.buyer_name} <span className="text-[#7C7064] text-xs">{c.buyer_email}</span></td>
                  <td className="p-3">{c.recipient_name || "—"}{isDirect && c.recipient_email && <div className="text-xs text-[#7C7064]">{c.recipient_email}</div>}</td>
                  <td className="p-3 text-xs">{c.product_name}</td>
                  <td className="p-3" data-testid={`paid-${c.code}`}><Paid cents={c.amount_paid} at={c.paid_at}/></td>
                  <td className="p-3 text-xs">{isDirect ? "Envoi direct" : "Impression"}</td>
                  <td className="p-3 text-xs text-[#7C7064]">{delivered}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-xs ${
                    c.state==="used" ? "bg-[#DCCBBA]" :
                    c.state==="expired" ? "bg-[#E8DCC8] text-[#A86962]" :
                    c.state==="pending" ? "bg-[#E8DCC8] text-[#7C7064]" :
                    "bg-[#7B8A72] text-white"}`}>{c.state}</span></td>
                  <td className="p-3">
                    <div className="flex gap-2 items-center">
                      {c.state !== "pending" && (
                        <a data-testid={`view-card-${c.code}`} href={`/carte-cadeau/print/${c.code}`} target="_blank" rel="noreferrer" className="text-xs underline text-[#A3876A]">Voir</a>
                      )}
                      {c.state === "active" && (
                        <button
                          data-testid={`resend-welcome-${c.code}`}
                          onClick={()=>doResendWelcome(c.code)}
                          className="text-xs underline text-[#A3876A] hover:text-[#2C2621]"
                          title="Renvoyer le mail de bienvenue"
                        >Renvoyer mail</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminOrders() {
  const [items, setItems] = useState([]);
  const [stripeAcct, setStripeAcct] = useState(null);
  useEffect(() => {
    api.get("/admin/orders").then(r => setItems(r.data.items));
    api.get("/admin/stripe/account").then(r => setStripeAcct(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl">Commandes</h1>
        <button data-testid="orders-export" onClick={() => downloadCsv("/admin/export/orders.csv", "commandes.csv").catch(e => toast.error(`Export impossible (${e.message})`))} className="btn-outline">Export CSV</button>
      </div>
      {stripeAcct && (
        <div data-testid="stripe-account-banner" className="mb-6 p-4 border border-[#E8DCC8] bg-[#F5F0E6] text-sm">
          <p className="text-xs uppercase tracking-wider text-[#7C7064] mb-1">Compte Stripe relié au site</p>
          {stripeAcct.enabled ? (
            <p className="text-[#2C2621]">
              <strong>{stripeAcct.business_name || "—"}</strong> · {stripeAcct.email || "email non renseigné"} ·
              <span className="text-xs text-[#7C7064]"> {stripeAcct.account_id} · {stripeAcct.livemode ? "mode réel" : "mode test"}</span>
            </p>
          ) : <p className="text-[#A86962]">{stripeAcct.error || "Stripe non configuré"}</p>}
        </div>
      )}
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Date</th><th className="p-3">Cliente</th><th className="p-3">Produit</th>
            <th className="p-3">Montant</th><th className="p-3">Paiement</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-[#7C7064]">Aucune commande.</td></tr>}
            {items.map(o => (
              <tr key={o.order_id} className="border-t border-[#E8DCC8]">
                <td className="p-3 text-xs">{new Date(o.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="p-3"><ClientLink email={o.email}>{o.customer_name || "—"}</ClientLink> <span className="text-xs text-[#7C7064]">{o.email}</span></td>
                <td className="p-3">{o.lookup_key}</td>
                <td className="p-3">{((o.amount||0)/100).toFixed(2)} €</td>
                <td className="p-3">{o.payment_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminInvoices() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/invoices").then(r => setItems(r.data.items || [])); }, []);
  const BASE = process.env.REACT_APP_BACKEND_URL;
  return (
    <div data-testid="admin-invoices">
      <h1 className="font-serif text-4xl mb-2">Factures</h1>
      <p className="text-sm text-[#7C7064] mb-8">
        Factures générées automatiquement à chaque paiement, envoyées par email et téléchargeables ci-dessous.
      </p>
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Date</th>
            <th className="p-3">N° facture</th>
            <th className="p-3">Cliente</th>
            <th className="p-3">Produit</th>
            <th className="p-3">Montant</th>
            <th className="p-3 text-right">PDF</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#7C7064]">Aucune facture pour le moment.</td></tr>}
            {items.map(inv => (
              <tr key={inv.invoice_id} className="border-t border-[#E8DCC8]" data-testid={`admin-invoice-${inv.invoice_number}`}>
                <td className="p-3 text-xs">{new Date(inv.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="p-3 font-mono text-xs">{inv.invoice_number}</td>
                <td className="p-3">{inv.customer_name || "—"} <span className="text-xs text-[#7C7064]">{inv.email}</span></td>
                <td className="p-3">{inv.product_name}</td>
                <td className="p-3">{((inv.amount||0)/100).toFixed(2)} €</td>
                <td className="p-3 text-right">
                  <a
                    href={`${BASE}/api/admin/invoices/${inv.invoice_id}/pdf`}
                    className="text-[#A3876A] hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-invoice-download-${inv.invoice_number}`}
                  >Télécharger →</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function buildEmailPreview(html) {
  const body = html && html.trim().length
    ? html
    : "<p style=\"padding:24px;color:#7C7064;font-style:italic\">Aperçu de l'email…</p>";
  return "<!doctype html><html><head><meta charset=\"utf-8\"><style>body{margin:0;font-family:Georgia,serif}</style></head><body>" + body + "</body></html>";
}

export function AdminEmails() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const load = () => api.get("/admin/emails").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  function open(item) { setEditing(item.key); setSubject(item.subject); setHtml(item.html); }
  async function save() {
    try { await api.put(`/admin/emails/${editing}`, { subject, html }); toast.success("Enregistré"); load(); setEditing(null); }
    catch { toast.error("Erreur"); }
  }
  return (
    <div data-testid="admin-emails">
      <h1 className="font-serif text-4xl mb-8">Emails transactionnels</h1>
      {!editing ? (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map(it => (
            <div key={it.key} className="bg-[#FDFBF7] p-6 border border-[#E8DCC8]">
              <p className="font-hand text-lg text-[#A3876A]">{it.key}</p>
              <h3 className="font-serif text-xl mb-2">{it.label}</h3>
              <p className="text-sm text-[#7C7064] mb-4 line-clamp-2">{it.subject}</p>
              <button data-testid={`edit-email-${it.key}`} onClick={()=>open(it)} className="btn-outline">Modifier</button>
            </div>
          ))}
        </div>
      ) : (
        <div className="max-w-3xl">
          <div className="flex items-center justify-between mb-4">
            <p className="font-hand text-lg text-[#A3876A]">Édition : {editing}</p>
            <button onClick={()=>setEditing(null)} className="text-sm text-[#7C7064] hover:text-[#2C2621]">← Retour à la liste</button>
          </div>
          <label className="text-sm text-[#7C7064] block mb-1">Objet de l'email</label>
          <input className="field-input mb-6" placeholder="Sujet" value={subject} onChange={e=>setSubject(e.target.value)}/>

          <label className="text-sm text-[#7C7064] block mb-1">Contenu de l'email</label>
          <EmailBodyEditor value={html} onChange={setHtml} testId="visual-editor-emails"/>
          <p className="text-xs text-[#7C7064] mt-3">
            Tu tapes exactement comme dans un traitement de texte. Les mises en forme sont conservées.
            Variables : <code>{"{{name}}"}</code>, <code>{"{{code}}"}</code>, <code>{"{{order_id}}"}</code>, <code>{"{{expires_at}}"}</code>, <code>{"{{postal_address}}"}</code>, <code>{"{{manage_url}}"}</code>
          </p>

          <div className="flex gap-3 mt-6 sticky bottom-4">
            <button data-testid="save-email" onClick={save} className="btn-primary">Enregistrer</button>
            <button onClick={()=>setEditing(null)} className="btn-outline">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminPages() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [content, setContent] = useState("");
  const load = () => api.get("/admin/pages").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  function open(it) { setEditing(it.page_id); setContent(JSON.stringify(it.content || {}, null, 2)); }
  async function save() {
    try {
      const parsed = JSON.parse(content);
      await api.put(`/admin/pages/${editing}`, { content: parsed });
      toast.success("Enregistré"); load(); setEditing(null);
    } catch (e) { toast.error("JSON invalide ou erreur"); }
  }
  return (
    <div data-testid="admin-pages">
      <h1 className="font-serif text-4xl mb-8">Pages</h1>
      {!editing ? (
        <div className="grid md:grid-cols-3 gap-4">
          {items.map(it => (
            <div key={it.page_id} className="bg-[#FDFBF7] p-6 border border-[#E8DCC8]">
              <h3 className="font-serif text-xl mb-4">{it.page_id}</h3>
              <button onClick={()=>open(it)} className="btn-outline">Modifier</button>
            </div>
          ))}
        </div>
      ) : (
        <div className="max-w-3xl">
          <p className="font-hand text-lg text-[#A3876A] mb-2">Édition : {editing}</p>
          <Textarea rows={16} value={content} onChange={e=>setContent(e.target.value)} className="font-mono text-sm bg-white border border-[#E8DCC8]"/>
          <div className="flex gap-3 mt-6">
            <button onClick={save} className="btn-primary">Enregistrer</button>
            <button onClick={()=>setEditing(null)} className="btn-outline">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminProgrammeEmails() {
  const [data, setData] = useState({ items: [], filled: 0, total: 52 });
  const [editing, setEditing] = useState(null);   // week number
  const [f, setF] = useState({ title: "", subject: "", html: "", mode: "guided", body_text: "", prompt: "" });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [draftInfo, setDraftInfo] = useState(null);
  const skipDraftRef = useRef(false);

  const load = () => api.get("/admin/programme-emails").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  async function openEdit(week) {
    const r = await api.get(`/admin/programme-emails/${week}`);
    const d = r.data;
    const mode = d.mode === "guided" ? "guided" : (d.html ? "html" : "guided");
    const fromServer = { title: d.title || "", subject: d.subject || `Semaine ${week} — `, html: d.html || defaultHtml(),
           mode, body_text: d.body_text || "", prompt: d.prompt || "" };
    let draft = null;
    try { draft = JSON.parse(localStorage.getItem(draftKey(week)) || "null"); } catch { draft = null; }
    const differs = draft && JSON.stringify({ ...draft, savedAt: undefined }) !== JSON.stringify({ ...fromServer, savedAt: undefined });
    if (differs) {
      setF({ ...fromServer, ...draft, savedAt: undefined });
      setDraftInfo(draft.savedAt);
    } else {
      setF(fromServer);
      setDraftInfo(null);
    }
    setPreview(null);
    setEditing(week);
  }

  function draftKey(week) { return `rdv_programme_draft_w${week}`; }

  useEffect(() => {
    if (editing === null) return;
    if (skipDraftRef.current) { skipDraftRef.current = false; return; }
    const t = setTimeout(() => {
      if (skipDraftRef.current) return;
      localStorage.setItem(draftKey(editing), JSON.stringify({ ...f, savedAt: new Date().toISOString() }));
    }, 600);
    return () => clearTimeout(t);
  }, [f, editing]);

  function discardDraft() {
    skipDraftRef.current = true;
    localStorage.removeItem(draftKey(editing));
    setDraftInfo(null);
    openEdit(editing);
  }

  function validate() {
    if (!f.subject) { toast.error("Sujet requis"); return false; }
    if (f.mode === "guided" && !f.body_text.trim()) { toast.error("Écris le texte de l'email"); return false; }
    if (f.mode === "html" && !f.html.trim()) { toast.error("Contenu requis"); return false; }
    return true;
  }

  async function doPreview() {
    if (!validate()) return;
    setBusy("preview");
    try {
      const r = await api.post(`/admin/programme-emails/${editing}/preview`, f);
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'aperçu"); } finally { setBusy(""); }
  }

  async function doTestSend() {
    if (!validate()) return;
    setBusy("test");
    try {
      const r = await api.post(`/admin/programme-emails/${editing}/test-send`, f);
      toast.success(`Email de test envoyé à ${r.data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi du test"); } finally { setBusy(""); }
  }

  function defaultHtml() {
    return `<table role="presentation" width="100%" style="background:#FDFBF7;font-family:Georgia,serif;color:#2C2621"><tr><td style="padding:32px;max-width:560px">
<p style="font-family:cursive;color:#A3876A;font-size:22px">Semaine [numéro]</p>
<h1>[Votre thème doux]</h1>
<p>[Votre invitation à écrire, quelques lignes.]</p>
<p style="background:#F5F0E6;padding:16px;font-style:italic">[Une phrase-guide pour démarrer]</p>
<p>[Un mot de clôture, une bienveillance]</p>
<p style="font-family:cursive;color:#A3876A;font-size:20px">— Amélie</p>
</td></tr></table>`;
  }

  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.put(`/admin/programme-emails/${editing}`, f);
      localStorage.removeItem(draftKey(editing));
      toast.success(`Semaine ${editing} enregistrée`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  }

  if (editing !== null) {
    const modeBtn = (m, label) => (
      <button type="button" data-testid={`pe-mode-${m}`} onClick={()=>setF({...f, mode:m})}
        className={`px-4 py-2 text-sm border ${f.mode===m ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]" : "border-[#E8DCC8] text-[#4A4036] hover:bg-[#FDFBF7]"}`}>
        {label}
      </button>
    );
    return (
      <div data-testid="admin-programme-email-editor" className="max-w-6xl">
        <p className="font-hand text-xl text-[#A3876A] mb-2">Semaine {editing}</p>
        <h1 className="font-serif text-4xl mb-8">Édition de l'email</h1>
        {draftInfo && (
          <div data-testid="pe-draft-banner" className="mb-6 p-4 border border-[#A3876A] bg-[#F5F0E6] flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[#2C2621]">Brouillon non enregistré restauré <span className="text-[#7C7064]">(dernière frappe : {new Date(draftInfo).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })})</span></p>
            <button data-testid="pe-discard-draft" type="button" onClick={discardDraft} className="text-xs text-[#A86962] hover:underline">Ignorer le brouillon et revenir à la version enregistrée</button>
          </div>
        )}
        <p className="text-xs text-[#7C7064] -mt-5 mb-6">Ton texte est sauvegardé automatiquement dans ce navigateur pendant que tu écris.</p>

        <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-6 bg-[#F5F0E6] p-8 border border-[#E8DCC8]">
          <div className="flex gap-2">
            {modeBtn("guided", "Rédaction guidée")}
            {modeBtn("html", "Éditeur libre")}
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Sujet de l'email</label>
            <input data-testid="pe-subject" className="field-input" required placeholder="Ex : Semaine 21 — ce qui te fait rire" value={f.subject} onChange={e=>setF({...f, subject:e.target.value})}/>
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">{f.mode === "guided" ? "Titre affiché en haut de l'email" : "Titre interne (n'apparaît pas dans l'email)"}</label>
            <input data-testid="pe-title" className="field-input" placeholder="Ex : Ce qui me fait rire" value={f.title} onChange={e=>setF({...f, title:e.target.value})}/>
          </div>

          {f.mode === "guided" ? (
            <>
              <div>
                <label className="text-sm text-[#7C7064] block mb-1">Ton texte (sépare les paragraphes par une ligne vide)</label>
                <textarea data-testid="pe-body-text" className="field-input min-h-[320px] font-serif text-base leading-relaxed" placeholder="Raconte ton expérience du thème, comme pour les semaines 1 à 20…" value={f.body_text} onChange={e=>setF({...f, body_text:e.target.value})}/>
              </div>
              <div>
                <label className="text-sm text-[#7C7064] block mb-1">Consigne d'écriture (encadré beige en italique)</label>
                <textarea data-testid="pe-prompt" className="field-input min-h-[90px] font-serif italic" placeholder="Et toi, … ?" value={f.prompt} onChange={e=>setF({...f, prompt:e.target.value})}/>
              </div>
              <p className="text-xs text-[#7C7064]">La signature « Je t'ai donné mes mots, à toi de trouver les tiens. », le mot de clôture et le pied de page sont ajoutés automatiquement, à l'identique des semaines 1 à 20.</p>
            </>
          ) : (
            <div>
              <label className="text-sm text-[#7C7064] block mb-1">Contenu de l'email</label>
              <EmailBodyEditor value={f.html} onChange={(html)=>setF({...f, html})} testId="visual-editor-programme"/>
              <p className="text-xs text-[#7C7064] mt-3">
                Variable disponible : <code>{"{{name}}"}</code> (remplacée par le prénom de la lectrice).
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <button data-testid="pe-preview" type="button" onClick={doPreview} disabled={!!busy} className="btn-outline">{busy==="preview" ? "…" : "Aperçu"}</button>
            <button data-testid="pe-test-send" type="button" onClick={doTestSend} disabled={!!busy} className="btn-outline">{busy==="test" ? "…" : "M'envoyer un test"}</button>
            <button data-testid="pe-save" onClick={save} disabled={saving} className="btn-primary">{saving ? "…" : "Enregistrer"}</button>
            <button onClick={()=>setEditing(null)} className="btn-outline">Retour à la liste</button>
          </div>
        </div>

        <div className="lg:col-span-2">
          <p className="text-xs uppercase tracking-wider text-[#7C7064] mb-2">Aperçu</p>
          {preview ? (
            <div data-testid="pe-preview-pane" className="border border-[#E8DCC8] bg-white">
              <p className="text-sm px-4 py-2 border-b border-[#E8DCC8] text-[#4A4036]"><span className="text-[#7C7064]">Objet :</span> {preview.subject}</p>
              <iframe title="Aperçu email" srcDoc={preview.html} className="w-full h-[720px] bg-white" sandbox=""/>
            </div>
          ) : (
            <div className="border border-dashed border-[#E8DCC8] p-8 text-sm text-[#7C7064] text-center">Clique sur « Aperçu » pour voir l'email tel qu'il sera reçu.</div>
          )}
        </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-programme-emails">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-hand text-xl text-[#A3876A]">Les 52 emails hebdomadaires</p>
          <h1 className="font-serif text-4xl">Programme d'écriture</h1>
        </div>
        <div data-testid="pe-progress" className="text-right">
          <p className="font-serif text-4xl">{data.filled}<span className="text-[#7C7064]"> / {data.total}</span></p>
          <p className="text-xs text-[#7C7064]">Emails rédigés</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.items.map(it => (
          <button key={it.week} data-testid={`pe-week-${it.week}`} onClick={()=>openEdit(it.week)}
            className={`text-left p-4 border transition-colors ${it.filled ? "border-[#A3876A] bg-[#FDFBF7]" : "border-dashed border-[#E8DCC8] bg-[#F5F0E6] hover:bg-[#FDFBF7]"}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-hand text-lg text-[#A3876A]">Semaine {it.week}</p>
                <p className="font-serif text-lg mt-1 line-clamp-1">{it.title || (it.filled ? "(sans titre)" : "à écrire")}</p>
                {it.subject && <p className="text-xs text-[#7C7064] mt-1 line-clamp-1">{it.subject}</p>}
              </div>
              <span className={`text-xs px-2 py-0.5 ${it.filled ? "bg-[#7B8A72] text-white" : "text-[#7C7064]"}`}>
                {it.filled ? "✓" : "vide"}
              </span>
            </div>
          </button>
        ))}
      </div>

      <p className="text-xs text-[#7C7064] mt-6">
        Chaque email est envoyé automatiquement à la semaine correspondante après l'inscription au programme.
        Rédigez-les à votre rythme — vous pouvez les modifier à tout moment.
      </p>
    </div>
  );
}

export function AdminPromoCodes() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({
    code: "", discount_type: "percent", value: 10,
    max_redemptions: "", expires_at: "",
    applicable_products: [],
  });
  const [creating, setCreating] = useState(false);
  const PRODUCTS = [
    { lk: "programme_seul", label: "Programme 102€" },
    { lk: "programme_coffret", label: "Programme + coffret 152€" },
    { lk: "correspondance_simple", label: "Correspondance 35€" },
    { lk: "correspondance_coffret", label: "Correspondance + coffret 59€" },
    { lk: "abonnement_correspondance", label: "Abonnement 27€/mois" },
  ];
  const load = () => api.get("/admin/promo-codes").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  function toggleProd(lk) {
    setF(prev => ({
      ...prev,
      applicable_products: prev.applicable_products.includes(lk)
        ? prev.applicable_products.filter(x => x !== lk)
        : [...prev.applicable_products, lk],
    }));
  }

  async function create(e) {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        code: f.code || undefined,
        discount_type: f.discount_type,
        value: parseInt(f.value, 10),
        max_redemptions: f.max_redemptions ? parseInt(f.max_redemptions, 10) : undefined,
        expires_at: f.expires_at ? new Date(f.expires_at + "T23:59:59Z").toISOString() : undefined,
        applicable_products: f.applicable_products.length ? f.applicable_products : undefined,
      };
      const r = await api.post("/admin/promo-codes", payload);
      toast.success(`Code créé : ${r.data.code}`);
      setF({ code: "", discount_type: "percent", value: 10, max_redemptions: "", expires_at: "", applicable_products: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setCreating(false); }
  }

  async function deactivate(promoId, code) {
    if (!window.confirm(`Désactiver le code ${code} ?`)) return;
    try {
      await api.patch(`/admin/promo-codes/${promoId}/deactivate`);
      toast.success("Code désactivé");
      load();
    } catch { toast.error("Erreur"); }
  }

  return (
    <div data-testid="admin-promo-codes">
      <h1 className="font-serif text-4xl mb-8">Codes promo</h1>

      {/* Create form */}
      <form onSubmit={create} className="bg-[#F5F0E6] p-8 border border-[#E8DCC8] mb-10">
        <p className="font-hand text-xl text-[#A3876A] mb-4">Créer un code promo</p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Code (laisser vide pour génération auto)</label>
            <input data-testid="promo-code" className="field-input" placeholder="Ex : NOEL2026" value={f.code} onChange={e=>setF({...f, code:e.target.value.toUpperCase()})}/>
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Type de remise</label>
            <select data-testid="promo-type" value={f.discount_type} onChange={e=>setF({...f, discount_type:e.target.value})}
              className="w-full bg-transparent border-b border-[#E8DCC8] py-2 outline-none">
              <option value="percent">Pourcentage (%)</option>
              <option value="amount">Montant fixe (€)</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Valeur</label>
            <input data-testid="promo-value" className="field-input" type="number" min="1" required
              placeholder={f.discount_type === "percent" ? "20 (=20%)" : "500 (=5€ soit 500 centimes)"}
              value={f.value} onChange={e=>setF({...f, value:e.target.value})}/>
            <p className="text-xs text-[#7C7064] mt-1">{f.discount_type === "percent" ? "En %" : "En centimes (100 = 1€)"}</p>
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Nombre d'utilisations max (facultatif)</label>
            <input data-testid="promo-max" className="field-input" type="number" min="1" placeholder="Illimité si vide" value={f.max_redemptions} onChange={e=>setF({...f, max_redemptions:e.target.value})}/>
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Date d'expiration (facultatif)</label>
            <input data-testid="promo-expires" className="field-input" type="date" min={new Date().toISOString().slice(0,10)} value={f.expires_at} onChange={e=>setF({...f, expires_at:e.target.value})}/>
          </div>
        </div>
        <div>
          <p className="text-sm text-[#7C7064] mb-2">Produits éligibles (aucun = tous)</p>
          <div className="flex flex-wrap gap-2">
            {PRODUCTS.map(p => (
              <label key={p.lk} className={`px-3 py-1.5 border cursor-pointer text-sm ${f.applicable_products.includes(p.lk) ? "border-[#A3876A] bg-[#FDFBF7]" : "border-[#E8DCC8]"}`}>
                <input type="checkbox" checked={f.applicable_products.includes(p.lk)} onChange={()=>toggleProd(p.lk)} className="sr-only"/>
                {p.label}
              </label>
            ))}
          </div>
        </div>
        <button data-testid="promo-create" disabled={creating} className="btn-primary mt-6">{creating ? "Création…" : "Créer le code"}</button>
      </form>

      {/* List */}
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Code</th><th className="p-3">Remise</th><th className="p-3">Utilisations</th>
            <th className="p-3">Expire</th><th className="p-3">Produits</th><th className="p-3">État</th><th className="p-3">Action</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-[#7C7064]">Aucun code promo pour l'instant.</td></tr>}
            {items.map(c => (
              <tr key={c.promotion_code_id} className="border-t border-[#E8DCC8]">
                <td className="p-3 font-mono">{c.code}</td>
                <td className="p-3">{c.discount_type === "percent" ? `${c.value}%` : `${(c.value/100).toFixed(2)}€`}</td>
                <td className="p-3">{c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                <td className="p-3 text-xs text-[#7C7064]">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td className="p-3 text-xs text-[#7C7064]">{c.applicable_products?.length ? c.applicable_products.join(", ") : "Tous"}</td>
                <td className="p-3"><span className={`px-2 py-0.5 text-xs ${c.active ? "bg-[#7B8A72] text-white" : "bg-[#DCCBBA]"}`}>{c.active ? "Actif" : "Inactif"}</span></td>
                <td className="p-3">
                  {c.active && <button data-testid={`deactivate-${c.code}`} onClick={()=>deactivate(c.promotion_code_id, c.code)} className="text-xs text-[#A86962] hover:underline">Désactiver</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[#7C7064] mt-4">
        Les clientes saisissent le code directement sur la page de paiement Stripe. La remise est appliquée automatiquement. Vous pouvez suivre les utilisations en temps réel.
      </p>
    </div>
  );
}

export function AdminSubscriptions() {
  const [data, setData] = useState({ items: [], count_this_month: 0, max: 20 });
  const load = () => api.get("/admin/subscriptions").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const cancel = async (sub) => {
    const immediate = window.confirm(
      `Résilier l'abonnement de ${sub.customer_name || sub.email} ?\n\n` +
      `OK = Résiliation IMMÉDIATE (elle perd l'accès aujourd'hui)\n` +
      `Annuler puis Résilier = Résiliation à la fin de la période en cours (plus doux)\n\n` +
      `Clique OK pour immédiat, Annuler pour choisir la version douce.`
    );
    if (immediate === null) return;
    try {
      await api.post(`/admin/subscriptions/${sub.subscription_id}/cancel?immediate=${immediate}`);
      toast.success(immediate ? "Résiliation immédiate effectuée" : "Résiliation programmée pour la fin de la période");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const removeRecord = async (sub) => {
    if (!window.confirm(`Supprimer définitivement l'historique de ${sub.customer_name || sub.email} de la table Abonnements ?\n\nLa cliente reste dans Commandes et Factures — seul l'enregistrement d'abonnement disparaît.`)) return;
    try {
      await api.delete(`/admin/subscriptions/${sub.subscription_id}`);
      toast.success("Enregistrement supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="admin-subscriptions">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-hand text-xl text-[#A3876A]">Nouveaux ce mois-ci</p>
          <h1 className="font-serif text-4xl">Abonnements</h1>
        </div>
        <div data-testid="sub-counter" className="text-right">
          <p className="font-serif text-4xl">{data.count_this_month}<span className="text-[#7C7064]"> / {data.max}</span></p>
        </div>
      </div>
      <p className="text-sm text-[#7C7064] mb-6 italic">
        En cas de résiliation par la cliente (via Stripe ou son espace privé), le statut passe automatiquement à "canceled". Les boutons ci-dessous te permettent de <strong>résilier toi-même</strong> ou de <strong>nettoyer l'historique</strong> une fois résilié.
      </p>
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Cliente</th><th className="p-3">Email</th><th className="p-3">Statut</th>
            <th className="p-3">Mois versés</th><th className="p-3">Prochain prélèv.</th><th className="p-3">Débuté</th>
            <th className="p-3">Actions</th>
          </tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-[#7C7064]">Aucun abonnement pour le moment.</td></tr>}
            {data.items.map(s => (
              <tr key={s.subscription_id} className="border-t border-[#E8DCC8]">
                <td className="p-3">{s.customer_name || "—"}</td>
                <td className="p-3 text-[#7C7064] text-xs">{s.email}</td>
                <td className="p-3"><span className={`px-2 py-0.5 text-xs ${
                  s.status==="active" ? "bg-[#7B8A72] text-white" :
                  s.status==="cancel_scheduled" ? "bg-[#E8DCC8] text-[#A86962]" :
                  s.status==="canceled" ? "bg-[#DCCBBA]" : "bg-[#E8DCC8]"}`}>{s.status}</span></td>
                <td className="p-3">{s.paid_invoices_count || 0}</td>
                <td className="p-3 text-xs text-[#7C7064]">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString("fr-FR") : "—"}</td>
                <td className="p-3 text-xs text-[#7C7064]">{s.started_at ? new Date(s.started_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td className="p-3">
                  <div className="flex gap-3 text-xs">
                    {(s.status === "active" || s.status === "cancel_scheduled") && (
                      <button
                        data-testid={`cancel-sub-${s.subscription_id}`}
                        onClick={()=>cancel(s)}
                        className="text-[#A86962] hover:underline"
                        title="Résilier via Stripe"
                      >Résilier</button>
                    )}
                    {(s.status === "canceled" || s.status === "cancel_scheduled") && (
                      <button
                        data-testid={`delete-sub-${s.subscription_id}`}
                        onClick={()=>removeRecord(s)}
                        className="text-[#7C7064] hover:text-[#A86962] hover:underline"
                        title="Supprimer l'entrée de la table"
                      >Supprimer</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminSettings() {
  const [s, setS] = useState({ sales_open_correspondance: true, sales_open_programme: true, sales_open_subscription: true, postal_address: "" });
  useEffect(() => { api.get("/admin/settings").then(r => setS(prev => ({ ...prev, ...r.data }))); }, []);
  async function save() {
    try { await api.put("/admin/settings", s); toast.success("Enregistré"); }
    catch { toast.error("Erreur"); }
  }
  return (
    <div data-testid="admin-settings" className="max-w-2xl">
      <h1 className="font-serif text-4xl mb-8">Réglages</h1>
      <div className="space-y-6 bg-[#FDFBF7] p-8 border border-[#E8DCC8]">
        <label className="flex items-center justify-between">
          <span>Ventes correspondance ouvertes</span>
          <input data-testid="toggle-corr" type="checkbox" checked={!!s.sales_open_correspondance} onChange={e=>setS({...s, sales_open_correspondance:e.target.checked})}/>
        </label>
        <label className="flex items-center justify-between">
          <span>Ventes programme ouvertes</span>
          <input data-testid="toggle-prog" type="checkbox" checked={!!s.sales_open_programme} onChange={e=>setS({...s, sales_open_programme:e.target.checked})}/>
        </label>
        <label className="flex items-center justify-between">
          <span>Abonnements ouverts</span>
          <input data-testid="toggle-sub" type="checkbox" checked={!!s.sales_open_subscription} onChange={e=>setS({...s, sales_open_subscription:e.target.checked})}/>
        </label>
        <div>
          <label className="text-sm text-[#7C7064] block mb-2">Adresse postale (affichée sur emails)</label>
          <Textarea rows={4} value={s.postal_address||""} onChange={e=>setS({...s, postal_address:e.target.value})} className="bg-white border border-[#E8DCC8]"/>
        </div>
        <button data-testid="save-settings" onClick={save} className="btn-primary">Enregistrer</button>
      </div>
    </div>
  );
}
